import { ICommentModel } from '@fi-shared/models';
import { ObjectTypes } from '@fi-shared/types';
import { isDefined } from '@fi-shared/utils/is-defined';
import { fiItemsFlatten } from '@fi-shared/utils/fi-items-flatten';
import { CommentsState } from './comments.state';

interface ParentInfo {
    rootParent: ICommentModel | null;
    immediateParent: ICommentModel | null;
    rootIndex: number;
}

export class CommentsOperations {
    constructor(private state: CommentsState) {}

    private sortByDate = (comments: ICommentModel[]): ICommentModel[] =>
        [...comments].sort((a, b) => new Date(a.CreatedOn || '').getTime() - new Date(b.CreatedOn || '').getTime());

    private updateChildren = (comment: ICommentModel, children: ICommentModel[]): ICommentModel => ({
        ...comment,
        ChildItems: this.sortByDate(children),
    });

    private createEmptyComment = (comment: ICommentModel): ICommentModel => ({
        ...comment,
        ChildItems: [],
    });

    private collectNestedChildren(comment: ICommentModel): ICommentModel[] {
        const children: ICommentModel[] = [];

        const processChildren = (parentComment: ICommentModel): void => {
            parentComment.ChildItems?.forEach((child) => {
                children.push(this.createEmptyComment(child));
                processChildren(child);
            });
        };

        processChildren(comment);
        return children;
    }

    private getFlattenedComments(comments: ICommentModel[]): ICommentModel[] {
        return comments.map((rootComment) => {
            const children = this.collectNestedChildren(rootComment);
            const sortedChildren = this.sortByDate(children);

            return {
                ...rootComment,
                ChildItems: sortedChildren,
            };
        });
    }

    private getAllCommentsFlatten(comments: ICommentModel[]): ICommentModel[] {
        const allComments: ICommentModel[] = [];
        comments.forEach((comment) => {
            allComments.push(comment);
            if (comment.ChildItems?.length) {
                allComments.push(...comment.ChildItems);
            }
        });
        return allComments;
    }

    private findCommentById(comments: ICommentModel[], id: number): ICommentModel | null {
        for (const comment of comments) {
            if (comment.Id === id) {
                return comment;
            }
            if (comment.ChildItems?.length) {
                const found = this.findCommentById(comment.ChildItems, id);
                if (found) {
                    return found;
                }
            }
        }
        return null;
    }

    private findParentInfo(comments: ICommentModel[], childComment: ICommentModel): ParentInfo {
        if (!childComment.ParentId) {
            return { rootParent: null, immediateParent: null, rootIndex: -1 };
        }

        const rootIndex = comments.findIndex((c) => c.Id === childComment.ParentId);
        if (rootIndex !== -1) {
            const rootComment = comments[rootIndex];
            return { rootParent: rootComment, immediateParent: rootComment, rootIndex };
        }

        for (let i = 0; i < comments.length; i++) {
            const rootComment = comments[i];
            const immediateParent = this.findCommentById(rootComment.ChildItems || [], childComment.ParentId);

            if (immediateParent) {
                return { rootParent: rootComment, immediateParent, rootIndex: i };
            }
        }

        return { rootParent: null, immediateParent: null, rootIndex: -1 };
    }

    private findRootParentId(comment: ICommentModel, objectType: ObjectTypes, objectId: number): number {
        if (!comment.ParentId) {
            return comment.Id;
        }

        const { rootParent } = this.findParentInfo(this.state.getComments(objectType, objectId), comment);
        return rootParent?.Id || comment.ParentId;
    }

    private removeCommentFromTree(comments: ICommentModel[], commentToRemove: ICommentModel): ICommentModel[] {
        const idsToRemove = new Set<number>();

        const parentChildMap = new Map<number, number[]>();

        const buildParentChildMap = (commentsArray: ICommentModel[]) => {
            commentsArray.forEach((comment) => {
                if (comment.ParentId) {
                    if (!parentChildMap.has(comment.ParentId)) {
                        parentChildMap.set(comment.ParentId, []);
                    }
                    parentChildMap.get(comment.ParentId)!.push(comment.Id);
                }
                if (comment.ChildItems?.length) {
                    buildParentChildMap(comment.ChildItems);
                }
            });
        };

        buildParentChildMap(comments);

        const collectDescendantIds = (commentId: number) => {
            idsToRemove.add(commentId);
            const childIds = parentChildMap.get(commentId) || [];
            childIds.forEach((childId) => collectDescendantIds(childId));
        };

        const findAndCollectIds = (commentsArray: ICommentModel[]) => {
            for (const comment of commentsArray) {
                if (comment.Id === commentToRemove.Id) {
                    collectDescendantIds(comment.Id);
                    break;
                }
                if (comment.ChildItems?.length) {
                    findAndCollectIds(comment.ChildItems);
                }
            }
        };

        findAndCollectIds(comments);

        const filterComments = (commentsArray: ICommentModel[]): ICommentModel[] => {
            return commentsArray
                .filter((comment) => !idsToRemove.has(comment.Id))
                .map((comment) => ({
                    ...comment,
                    ChildItems: comment.ChildItems?.length ? filterComments(comment.ChildItems) : [],
                }));
        };

        return filterComments(comments);
    }

    private upsertRootComment(comments: ICommentModel[], rootComment: ICommentModel): ICommentModel[] {
        const existingIndex = comments.findIndex((c) => c.Id === rootComment.Id);
        if (existingIndex === -1) {
            return [...comments, { ...rootComment, ChildItems: [] }];
        }

        const updatedComments = [...comments];
        updatedComments[existingIndex] = {
            ...rootComment,
            ChildItems: comments[existingIndex].ChildItems || [],
        };
        return updatedComments;
    }

    private upsertChildComment(comments: ICommentModel[], childComment: ICommentModel): ICommentModel[] {
        const { rootParent, rootIndex } = this.findParentInfo(comments, childComment);

        if (!rootParent || rootIndex === -1) {
            console.warn(`Root parent not found for comment with ID ${childComment.Id}`);
            return comments;
        }

        const currentChildren = rootParent.ChildItems || [];
        const existingIndex = currentChildren.findIndex((c) => c.Id === childComment.Id);
        const updatedChildren = [...currentChildren];

        if (existingIndex === -1) {
            updatedChildren.push(this.createEmptyComment(childComment));
        } else {
            updatedChildren[existingIndex] = this.createEmptyComment(childComment);
        }

        const updatedRootComment = this.updateChildren(rootParent, updatedChildren);

        return [...comments.slice(0, rootIndex), updatedRootComment, ...comments.slice(rootIndex + 1)];
    }

    private upsertCommentInTree(comments: ICommentModel[], commentToUpsert: ICommentModel): ICommentModel[] {
        return !commentToUpsert.ParentId
            ? this.upsertRootComment(comments, commentToUpsert)
            : this.upsertChildComment(comments, commentToUpsert);
    }

    setComments(
        comments: ICommentModel[],
        commentsToShowLimit: number,
        objectType: ObjectTypes,
        objectId: number,
    ): void {
        const flattenedComments = this.getFlattenedComments(comments);
        const visibleComments = isDefined(commentsToShowLimit)
            ? flattenedComments.slice(0, commentsToShowLimit)
            : flattenedComments;

        this.state.getOrCreateState(objectType, objectId);

        this.state.updateState(objectType, objectId, {
            comments: flattenedComments,
            flattenAllComments: this.getAllCommentsFlatten(flattenedComments),
            visibleComments: visibleComments,
        });

        flattenedComments.forEach((comment) => {
            this.state.emitCommentsUpdated(comment.Id);
        });
    }

    handleRemoveComment(comment: ICommentModel, objectType: ObjectTypes, objectId: number): void {
        const currentState = this.state.getOrCreateState(objectType, objectId);
        const updatedComments = this.removeCommentFromTree(currentState.comments, comment);

        this.state.updateState(objectType, objectId, {
            comments: updatedComments,
            flattenAllComments: this.getAllCommentsFlatten(updatedComments),
            visibleComments: updatedComments,
        });

        const rootParentId = this.findRootParentId(comment, objectType, objectId);
        if (rootParentId) {
            this.state.emitCommentsUpdated(rootParentId);
        }
    }

    handleUpsertComment(comment: ICommentModel, objectType: ObjectTypes, objectId: number): void {
        const currentState = this.state.getOrCreateState(objectType, objectId);
        const updatedComments = this.upsertCommentInTree(currentState.comments, comment);

        this.state.updateState(objectType, objectId, {
            comments: updatedComments,
            flattenAllComments: this.getAllCommentsFlatten(updatedComments),
            visibleComments: updatedComments,
        });

        const rootParentId = this.findRootParentId(comment, objectType, objectId);
        if (rootParentId) {
            this.state.emitCommentsUpdated(rootParentId);
        }
    }

    showAllComments(objectType: ObjectTypes, objectId: number): void {
        const state = this.state.getOrCreateState(objectType, objectId);
        this.state.updateState(objectType, objectId, {
            visibleComments: state.comments,
        });
    }

    getCommentById(id: number, objectType: ObjectTypes, objectId: number): ICommentModel | null {
        const state = this.state.getOrCreateState(objectType, objectId);
        return this.findCommentById(state.comments, id);
    }

    flattenCommentsChildren(comment: ICommentModel): ICommentModel[] {
        return fiItemsFlatten<ICommentModel>(comment?.ChildItems ?? []);
    }
}
